import React, { useContext, useEffect, useRef, useState } from "react";

import {
  List,
  Map,
  Search,
  Tune,
  ViewModule,
  Image,
} from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputBase,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { postImageSearchPortal } from "@rubi/api-generated";
import { useTranslation } from "react-i18next";

import { OnlineReportingContext } from "@/components/online-reporting/context";
import SubmissionSortControl from "@/components/online-reporting/Search/SubmissionSortControl";
import { SubmissionSort } from "@/components/online-reporting/submissionSort";

import ImageSearchModal from "./ImageSearchModal";

export interface SearchSuggestion {
  id: string;
  groupKey: string;
  primaryText: string;
  secondaryText?: string;
  icon?: React.ReactNode;
}

export interface SearchSuggestionGroup {
  key: string;
  label: string;
  suggestions: SearchSuggestion[];
  searchAllLabel?: string;
}

type SuggestionOption =
  | { kind: "suggestion"; suggestion: SearchSuggestion }
  | { kind: "searchAll"; label: string };

interface SearchBarProps {
  getMoreItems: () => void;
  onSearch: () => void;
  onClearSearch: () => void;
  searchView: string;
  setSearchView: (value: "card" | "list" | "map") => void;
  defaultView: "card" | "list" | "map";
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  currentItems: any[];
  setCurrentItems: React.Dispatch<React.SetStateAction<any[]>>;
  filtersVisible: boolean;
  setFiltersVisible: React.Dispatch<React.SetStateAction<boolean>>;
  totalAlerts: number;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  searchType: "alert" | "report" | "registry";
  filterChips?: any;
  /** Show the filters button. Defaults to true; set false when there is no filter panel. */
  showFilters?: boolean;
  filtersRef: React.MutableRefObject<HTMLInputElement | null>;
  activeViews: ("card" | "list" | "map")[];
  sort?: SubmissionSort;
  onSortChange?: (sort: SubmissionSort) => void;
  /** Extra actions rendered inline next to the filters button. */
  toolbarActions?: React.ReactNode;
  suggestionGroups?: SearchSuggestionGroup[];
  suggestionsLoading?: boolean;
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  onDraftChange?: (draft: string) => void;
  noSuggestionsText?: string;
}

const SUGGESTION_LIST_ID = "search-bar-suggestions";
const suggestionOptionId = (index: number) =>
  `${SUGGESTION_LIST_ID}-option-${index}`;

const iconMapping = {
  card: <ViewModule style={{ marginRight: "4px" }} />,
  list: <List style={{ marginRight: "4px" }} />,
  map: <Map style={{ marginRight: "4px" }} />,
};

export const getSearchResultMessage = (
  loading: boolean,
  searchText: string,
  currentItemsLength: number,
  totalAlerts: number,
  t: (key: string, options?: any) => string,
): string => {
  if (loading) {
    return searchText
      ? t("loadingSearchResults")
      : currentItemsLength < totalAlerts
        ? t("loadingMoreItems", {
            currentItems: currentItemsLength,
            totalItems: totalAlerts,
          })
        : t("preparingResults");
  }

  if (searchText && totalAlerts === 0) {
    return t("noResultsFound");
  }

  if (currentItemsLength) {
    return searchText
      ? t("displayResults", { totalAlerts, searchText })
      : t("displayItems", {
          currentItems: currentItemsLength,
          totalItems: totalAlerts,
        });
  }

  if (currentItemsLength === 0 && totalAlerts === 0 && searchText === "") {
    return t("noResultsFound");
  }

  return "";
};

const SearchBar = ({
  onSearch,
  onClearSearch,
  searchView,
  setSearchView,
  defaultView,
  searchText,
  setSearchText,
  setCurrentItems,
  setFiltersVisible,
  loading,
  setLoading,
  filterChips,
  showFilters = true,
  filtersRef,
  activeViews,
  sort,
  onSortChange,
  toolbarActions,
  suggestionGroups,
  suggestionsLoading = false,
  onSuggestionSelect,
  onDraftChange,
  noSuggestionsText,
}: SearchBarProps) => {
  const [newSearchText, setNewSearchText] = useState(searchText || "");
  const [isImageSearchModalOpen, setIsImageSearchModalOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const searchFieldRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation("bulletin/searchbar");
  const {
    imageSearchID,
    setImageSearchID,
    currentSearchImageData,
    setCurrentSearchImageData,
    setImageAttachmentId,
    setImageSubmissionId,
  } = useContext(OnlineReportingContext);
  const suggestionsEnabled = Boolean(onSuggestionSelect);
  const allGroups = suggestionGroups ?? [];
  const renderableGroups = allGroups.filter(
    (group) => group.suggestions.length > 0 || group.searchAllLabel,
  );
  const options: SuggestionOption[] = allGroups.flatMap((group) => [
    ...group.suggestions.map(
      (suggestion) => ({ kind: "suggestion", suggestion }) as SuggestionOption,
    ),
    ...(group.searchAllLabel
      ? [{ kind: "searchAll", label: group.searchAllLabel } as SuggestionOption]
      : []),
  ]);
  const groupOptionOffsets: Record<string, number> = {};
  let optionCursor = 0;
  for (const group of allGroups) {
    groupOptionOffsets[group.key] = optionCursor;
    optionCursor += group.suggestions.length + (group.searchAllLabel ? 1 : 0);
  }
  const matchCount = allGroups.reduce(
    (total, group) => total + group.suggestions.length,
    0,
  );
  const showSuggestions =
    suggestionsEnabled &&
    suggestionsOpen &&
    newSearchText.trim().length > 0 &&
    (options.length > 0 || suggestionsLoading || Boolean(noSuggestionsText));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewSearchText(event.target.value);
    setActiveSuggestion(-1);
    setSuggestionsOpen(true);
    onDraftChange?.(event.target.value);
  };

  const handleSearchEnter = () => {
    if (newSearchText !== "") {
      setSearchText(newSearchText);
      setLoading(true);
      setCurrentItems([]);
      onSearch();
    }
  };

  const handleOptionSelect = (option: SuggestionOption) => {
    setSuggestionsOpen(false);
    setActiveSuggestion(-1);
    if (option.kind === "searchAll") {
      handleSearchEnter();
      return;
    }
    onSuggestionSelect?.(option.suggestion);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    const canNavigate = showSuggestions && options.length > 0;

    if (canNavigate && event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestion((prev) => (prev + 1) % options.length);
      return;
    }
    if (canNavigate && event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestion((prev) => (prev <= 0 ? options.length : prev) - 1);
      return;
    }
    if (event.key === "Escape" && showSuggestions) {
      event.preventDefault();
      setSuggestionsOpen(false);
      setActiveSuggestion(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const highlighted = options[activeSuggestion];
      if (showSuggestions && highlighted) {
        handleOptionSelect(highlighted);
        return;
      }
      setSuggestionsOpen(false);
      handleSearchEnter();
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setNewSearchText("");
    setSuggestionsOpen(false);
    setActiveSuggestion(-1);
    onDraftChange?.("");
    onClearSearch();
  };

  useEffect(() => {
    if (newSearchText === "" && !loading) {
      handleClearSearch();
    }
  }, [newSearchText]);

  const convertImageToJpegAndCompress = (
    imageDataUrl: string,
    quality: number = 0.8,
    maxWidth: number = 1920,
    maxHeight: number = 1920,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions if image exceeds max dimensions
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }

          // Draw image on canvas
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to JPEG with compression
          const jpegDataUrl = canvas.toDataURL("image/jpeg", quality);
          const base64Data = jpegDataUrl.split(",")[1];
          resolve(base64Data);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = imageDataUrl;
    });
  };

  const handleImageSearchConfirm = async (imageData: string) => {
    try {
      const base64Data = await convertImageToJpegAndCompress(imageData);
      const response = await postImageSearchPortal({
        body: {
          base64_data: base64Data,
        },
      });
      setImageSearchID(response?.data?.image_search_id ?? null);
      setCurrentSearchImageData(imageData);
      setIsImageSearchModalOpen(false);
    } catch (error) {
      console.error("Failed to process image:", error);
    }
  };

  const handleImageSearchClear = () => {
    setImageSearchID(null);
    setCurrentSearchImageData(null);
    setImageAttachmentId(null);
    setImageSubmissionId(null);
  };

  const handleImageSearchCancel = () => {
    setIsImageSearchModalOpen(false);
  };

  return (
    <div className="search-bar">
      <Box
        ref={filtersRef}
        className="search-bar-container"
        sx={{
          display: "flex",
          flexDirection: "row",
          // No `alignItems` on purpose: children stretch to the row height set
          // by the search Paper, which is what keeps Filters and the sort
          // toggle the same height as the search field.
          // 70% of the page on desktop; the search field flexes to fill it.
          maxWidth: { xs: "100%", md: "70%" },
          width: "100%",
        }}
      >
        <ClickAwayListener onClickAway={() => setSuggestionsOpen(false)}>
          <div style={{ flex: 1, minWidth: 200 }} ref={searchFieldRef}>
            <Paper
              component="form"
              elevation={4}
              sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                // Same corners as the Filters/Sort buttons; squared off at the
                // bottom while the suggestions list hangs below.
                borderRadius: 1,
                ...(showSuggestions && {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }),
                zIndex: 4,
                bgcolor: "var(--search-field-background)",
                // Paper paints a white elevation overlay in dark mode
                // (backgroundImage: var(--Paper-overlay)), which stacks on top of
                // bgcolor and overrides --search-field-background. Suppress it so
                // the pill is the colour the token specifies. The elevation={4}
                // box-shadow is separate and still applies.
                backgroundImage: "none",
                // Outlined in dark mode (designer's shell pass); transparent in light
                // so the field is the same size in both.
                border: "1px solid var(--search-field-border)",
              }}
            >
              <IconButton
                type="button"
                sx={{ p: "10px", color: "text.secondary" }}
                aria-label="search"
              >
                <Search />
              </IconButton>

              <InputBase
                sx={{
                  ml: 1,
                  flex: 1,
                  fontSize: "16px",
                  color: "text.primary",
                  // MUI's default placeholder is currentColor at 0.5 opacity (dark)
                  // / 0.42 (light), which measures 4.21:1 and 3.4:1 against this
                  // pill — both below WCAG AA. Set it explicitly instead.
                  "& .MuiInputBase-input::placeholder": {
                    color: "text.secondary",
                    opacity: 1,
                  },
                }}
                placeholder={t("search")}
                value={newSearchText}
                onChange={handleSearchChange}
                onFocus={() => suggestionsEnabled && setSuggestionsOpen(true)}
                onKeyDown={handleSearchKeyDown}
                inputProps={
                  suggestionsEnabled
                    ? {
                        role: "combobox",
                        "aria-expanded": showSuggestions,
                        "aria-controls": SUGGESTION_LIST_ID,
                        "aria-autocomplete": "list",
                        "aria-activedescendant":
                          showSuggestions && options[activeSuggestion]
                            ? suggestionOptionId(activeSuggestion)
                            : undefined,
                      }
                    : undefined
                }
              />

              <IconButton
                type="button"
                aria-label="clear"
                sx={{ p: "10px", color: "text.secondary" }}
                onClick={() => handleClearSearch()}
              >
                <Close />
              </IconButton>
            </Paper>

            <Popper
              open={showSuggestions}
              anchorEl={searchFieldRef.current}
              placement="bottom-start"
              style={{
                zIndex: 1300,
                width: searchFieldRef.current?.clientWidth,
              }}
            >
              <Paper
                elevation={4}
                sx={{
                  borderRadius: 1,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  py: 1,
                }}
              >
                <MenuList
                  id={SUGGESTION_LIST_ID}
                  role="listbox"
                  dense
                  disablePadding
                >
                  {matchCount === 0 && suggestionsLoading && (
                    <MenuItem disabled role="presentation">
                      <Typography variant="body2">
                        {t("loadingSearchResults")}
                      </Typography>
                    </MenuItem>
                  )}
                  {matchCount === 0 &&
                    !suggestionsLoading &&
                    noSuggestionsText && (
                      <MenuItem disabled role="presentation">
                        <Typography variant="body2">
                          {noSuggestionsText}
                        </Typography>
                      </MenuItem>
                    )}
                  {renderableGroups.flatMap((group) => {
                    const groupOffset = groupOptionOffsets[group.key] ?? 0;
                    const searchAllIndex =
                      groupOffset + group.suggestions.length;
                    const searchAllLabel = group.searchAllLabel;

                    return [
                      <ListSubheader
                        key={group.key}
                        role="presentation"
                        disableSticky
                        sx={{
                          background: "transparent",
                          color: "text.secondary",
                          fontSize: "12px",
                          lineHeight: "28px",
                          textTransform: "uppercase",
                        }}
                      >
                        {group.label}
                      </ListSubheader>,
                      ...group.suggestions.map((suggestion, position) => {
                        const index = groupOffset + position;
                        return (
                          <MenuItem
                            key={suggestion.id}
                            id={suggestionOptionId(index)}
                            role="option"
                            aria-selected={index === activeSuggestion}
                            selected={index === activeSuggestion}
                            onMouseEnter={() => setActiveSuggestion(index)}
                            onClick={() =>
                              handleOptionSelect({
                                kind: "suggestion",
                                suggestion,
                              })
                            }
                            sx={{ gap: 1.5, py: 1 }}
                          >
                            {suggestion.icon}
                            <div style={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {suggestion.primaryText}
                              </Typography>
                              {suggestion.secondaryText && (
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {suggestion.secondaryText}
                                </Typography>
                              )}
                            </div>
                          </MenuItem>
                        );
                      }),
                      searchAllLabel ? (
                        <MenuItem
                          key={`${group.key}-search-all`}
                          id={suggestionOptionId(searchAllIndex)}
                          role="option"
                          aria-selected={searchAllIndex === activeSuggestion}
                          selected={searchAllIndex === activeSuggestion}
                          onMouseEnter={() =>
                            setActiveSuggestion(searchAllIndex)
                          }
                          onClick={() =>
                            handleOptionSelect({
                              kind: "searchAll",
                              label: searchAllLabel,
                            })
                          }
                          sx={{ gap: 1.5, py: 1, color: "primary.main" }}
                        >
                          <Search fontSize="small" />
                          <Typography variant="body2" noWrap>
                            {searchAllLabel}
                          </Typography>
                        </MenuItem>
                      ) : null,
                    ];
                  })}
                </MenuList>
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
        {showFilters && (
          <Button
            className="ml-3"
            sx={{ width: "200px", padding: "0" }}
            variant="outlined"
            onClick={() => setFiltersVisible(true)}
          >
            <Tune />
            <span className="ml-2 d-none d-md-block">{t("filters")}</span>
          </Button>
        )}
        {toolbarActions}
        {sort && onSortChange && (
          <SubmissionSortControl
            sort={sort}
            onSortChange={onSortChange}
            disabledReason={
              imageSearchID || currentSearchImageData
                ? t("sortedBySimilarity")
                : undefined
            }
          />
        )}
        {window.location.pathname.includes("rubiintel") && (
          <Button
            className="ml-3"
            sx={{ padding: "0 12px", whiteSpace: "nowrap", flexShrink: 0 }}
            variant={
              imageSearchID || currentSearchImageData ? "contained" : "outlined"
            }
            onClick={() => setIsImageSearchModalOpen(true)}
          >
            <Image />
            <span className="ml-2 d-none d-md-block">{t("imageSearch")}</span>
          </Button>
        )}
      </Box>

      <ImageSearchModal
        open={isImageSearchModalOpen}
        onClose={handleImageSearchCancel}
        onConfirm={handleImageSearchConfirm}
        onClear={handleImageSearchClear}
        initialImageData={currentSearchImageData}
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginTop: "8px",
        }}
      >
        {filterChips}
      </div>
      {/* View tabs. Skipped when there is only one view, since the empty
          strip would render as a stray divider line above the list. */}
      {activeViews && activeViews.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "self-start",
            borderBottom: 1,
            borderColor: "divider",
            padding: "10px 0",
            fontSize: "14px",
            color: "action.active",
          }}
        >
          {activeViews && activeViews.length > 1 && (
            <Button
              sx={{
                marginRight: "24px",
                textTransform: "none",
                fontWeight: searchView === defaultView ? "bold" : "normal",
                color:
                  searchView === defaultView ? "primary.main" : "action.active",
                borderBottom:
                  searchView === defaultView
                    ? "2px solid var(--primary-main)"
                    : "none",
                paddingBottom: "6px",
                "&:hover": {
                  borderBottom: "2px solid var(--primary-main)",
                  paddingBottom: "6px",
                },
              }}
              onClick={() => setSearchView(defaultView)}
            >
              {iconMapping[defaultView]}
              {t(`${defaultView}View`)}
            </Button>
          )}

          {activeViews && activeViews.length > 1 && defaultView !== "card" && (
            <Button
              sx={{
                marginRight: "24px",
                textTransform: "none",
                fontWeight: searchView === "card" ? "bold" : "normal",
                color: searchView === "card" ? "primary.main" : "action.active",
                borderBottom:
                  searchView === "card"
                    ? "2px solid var(--primary-main)"
                    : "none",
                paddingBottom: "6px",
                "&:hover": {
                  borderBottom: "2px solid var(--primary-main)",
                  paddingBottom: "6px",
                },
              }}
              onClick={() => setSearchView("card")}
            >
              <ViewModule style={{ marginRight: "4px" }} />
              {t("cardView")}
            </Button>
          )}

          {activeViews && activeViews.length > 1 && defaultView !== "map" && (
            <Button
              sx={{
                marginRight: "24px",
                textTransform: "none",
                fontWeight: searchView === "map" ? "bold" : "normal",
                color: searchView === "map" ? "primary.main" : "action.active",
                borderBottom:
                  searchView === "map"
                    ? "2px solid var(--primary-main)"
                    : "none",
                paddingBottom: "6px",
                "&:hover": {
                  borderBottom: "2px solid var(--primary-main)",
                  paddingBottom: "6px",
                },
              }}
              onClick={() => setSearchView("map")}
            >
              <Map style={{ marginRight: "4px" }} />
              {t("mapView")}
            </Button>
          )}
          {activeViews && activeViews.length > 1 && defaultView !== "list" && (
            <Button
              sx={{
                marginRight: "24px",
                textTransform: "none",
                fontWeight: searchView === "list" ? "bold" : "normal",
                color: searchView === "list" ? "primary.main" : "action.active",
                borderBottom:
                  searchView === "list"
                    ? "2px solid var(--primary-main)"
                    : "none",
                paddingBottom: "6px",
                "&:hover": {
                  borderBottom: "2px solid var(--primary-main)",
                  paddingBottom: "6px",
                },
              }}
              onClick={() => setSearchView("list")}
            >
              <List style={{ marginRight: "4px" }} />
              {t("listView")}
            </Button>
          )}
        </Box>
      )}
      {/* 
      <div
        style={{
          fontSize: "14px",
          color: colors.action.active,
          marginBottom: "8px",
        }}
      >
        {searchResultMessage}
      </div> */}
    </div>
  );
};

export default SearchBar;
